import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { Subscription, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { environment } from 'src/environments/environment';

import { Document } from 'src/app/shared/document';
import { ClientApiService } from 'src/app/services/client-api.service';

/**
 * @description
 * Renders the seller-side party panel for inventory-type documents. Functionally
 * similar to `EditDocumentCompanyComponent` but additionally synchronises
 * `DOCCLIDEPID` and `DOCPCODEPID` alongside `DOCLCODEPID` when the main stock depot
 * changes, and emits a `changes` event after each field mutation so the parent
 * `EditDocumentComponent` can propagate the update.
 *
 * All API subscriptions use `takeUntil(destroy$)` for automatic teardown on destroy.
 *
 * ### Inputs / Outputs
 * | Member           | Type            | Description                              |
 * |------------------|-----------------|------------------------------------------|
 * | `document`       | Input\<Document\> | The shared document being edited         |
 * | `manCliLabel`    | Input\<string\>   | Label for main-company selector          |
 * | `manPerLabel`    | Input\<string\>   | Label for main-contact selector          |
 * | `manDepLabel`    | Input\<string\>   | Label for main-stock selector            |
 * | `lcoCliLabel`    | Input\<string\>   | Label for LCO company selector           |
 * | `lcoPerLabel`    | Input\<string\>   | Label for LCO contact selector           |
 * | `lcoDepLabel`    | Input\<string\>   | Label for LCO stock selector             |
 * | `changes`        | Output\<Document\>| Emitted after any field mutation         |
 */
@Component({
  selector: 'app-edit-document-company-inventory',
  templateUrl: './edit-document-company-inventory.component.html',
  styleUrls: ['./edit-document-company-inventory.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class EditDocumentCompanyInventoryComponent implements OnInit, OnChanges, OnDestroy {

  @Input() document: Document;

  @Input() manCliLabel: string = 'Основная организация продавца';
  @Input() manPerLabel: string = 'Контактное лицо';
  @Input() manDepLabel: string = 'Основной склад';
  @Input() lcoCliLabel: string = 'Юридическое лицо продавца';
  @Input() lcoPerLabel: string = 'Ответственный';
  @Input() lcoDepLabel: string = 'Склад';

  private _depChildQuery: Subscription;
  private destroy$ = new Subject<void>();

  @Output() changes: EventEmitter<Document> = new EventEmitter();

  mainCompanyId: number;

  constructor(
    private cliApi: ClientApiService,
  ) { }

  ngOnChanges(changes: SimpleChanges) {
    //console.log('*** EditDocumentCompanyInventoryComponent ngOnChanges:');
    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** EditDocumentCompanyInventoryComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //console.log('*** EditDocumentCompanyInventoryComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {

        case 'document':

          if (this.mainCompanyId !== change.currentValue.MANCLIID) {
            this.mainCompanyId = change.currentValue.MANCLIID;
          }
          break;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('*** EditDocumentCompanyInventoryComponent in ngOnDestroy');
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    console.log('*** EditDocumentCompanyInventoryComponent in ngOnInit')
  }

  /** @description Called when the user selects a main company. Emits the updated document. */
  mainCompanySelected(selectedId) {
    console.log('*** EditDocumentCompanyInventoryComponent in mainCompanySelected: ' + selectedId);
    this.changes.emit(this.document);
  }

  /** @description Called when the user selects a contact person for the main company. Writes `selectedId` to `document.DOCMANPERID` and emits changes. */
  mainPersonSelected(selectedId) {
    console.log('*** EditDocumentCompanyInventoryComponent in mainPersonSelected: ' + selectedId);

    this.document.DOCMANPERID = selectedId;
    this.changes.emit(this.document);
  }

  /**
   * @description
   * Called when the user selects a main stock depot. Sets `document.DOCMANDEPID` and
   * `document.DOCCLIDEPID` to `selectedId`, then queries
   * `ClientApiService.getChildDepartmentsById()` to resolve the matching LCO/PCO child
   * department and patches both `document.DOCLCODEPID` and `document.DOCPCODEPID`.
   * Emits `changes` on success. The subscription is torn down via `takeUntil(destroy$)`
   * and errors are logged to console.
   */
  mainStockSelected(selectedId) {
    console.log('*** EditDocumentCompanyInventoryComponent in mainStockSelected: ' + selectedId);

    this.document.DOCMANDEPID = selectedId;
    this.document.DOCCLIDEPID = selectedId;

    this._depChildQuery =
      this.cliApi.getChildDepartmentsById(this.document.DOCMANDEPID, this.document.DOCLCOCLIID)
      .pipe(
        map(data => {
          this.document.DOCLCODEPID = data[0].CLIID;
          this.document.DOCPCODEPID = data[0].CLIID;
          this.changes.emit(this.document);
        }),
        takeUntil(this.destroy$)
      )
        .subscribe({
          error: err => console.error('*** EditDocumentCompanyInventoryComponent _depChildQuery error:', err)
        });
  }

  /** @description Called when the user selects an LCO company. Writes `selectedId` to `document.DOCLCOCLIID` and emits changes. */
  lcoCompanySelected(selectedId) {
    console.log('*** EditDocumentCompanyInventoryComponent in lcoCompanySelected: ' + selectedId);
    this.document.DOCLCOCLIID = selectedId;
    this.changes.emit(this.document);
  }

  /** @description Called when the user selects a contact person for the LCO entity. Writes `selectedId` to `document.DOCLCOPERID` and emits changes. */
  lcoPersonSelected(selectedId) {
    console.log('*** EditDocumentCompanyInventoryComponent in lcoPersonSelected: ' + selectedId);
    this.document.DOCLCOPERID = selectedId;
    this.changes.emit(this.document);
  }

  /** @description Called when the user selects an LCO stock depot. Writes `selectedId` to `document.DOCLCODEPID` and emits changes. */
  lcoStockSelected(selectedId) {
    console.log('*** EditDocumentCompanyInventoryComponent in lcoStockSelected: ' + selectedId);
    this.document.DOCLCODEPID = selectedId;
    this.changes.emit(this.document);
  }
}
